// Explore-stats page: QT time-of-day (#212).
// Surfaces when each captured question actually began (the
// `start_time` field on `house_questions`). The flagship view is a scatter
// of question_number × minutes-after-14:00, coloured by sitting day, plus
// supporting per-day earliest/latest/pace, start-time-drift trend, and an
// outliers list for questions that began outside the standard window.
//
// `start_time` completeness depends on the HouseLive admin marking each day
// as start-time-complete — we surface a completeness caveat on the page.

import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper, Typography, Grid, Box, Card, CardContent, Stack, Alert,
} from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatLine, StatBar, PALETTE } from './charts';
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';

function StatCard({ label, value, sub }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ mt: 0.5, lineHeight: 1.1 }}>
          {value ?? '—'}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// Scatter of question_number vs mins-after-14:00, one Recharts series per
// sitting day so the legend acts as a day selector.
function QtScatter({ points }) {
  const theme = useTheme();
  const byDate = useMemo(() => {
    const groups = {};
    (points || []).forEach((p) => {
      if (!groups[p.date]) groups[p.date] = [];
      groups[p.date].push(p);
    });
    return groups;
  }, [points]);
  const dates = Object.keys(byDate).sort();
  const tick = { fontSize: 12, fill: theme.palette.text.secondary };
  return (
    <ResponsiveContainer width="100%" height={420}>
      <ScatterChart margin={{ top: 8, right: 16, bottom: 16, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
        <XAxis type="number" dataKey="mins_after_14"
               name="Minutes after 14:00"
               unit=" min" tick={tick}
               stroke={theme.palette.text.secondary}
               label={{ value: 'Minutes after 14:00',
                         position: 'insideBottom', offset: -5,
                         fill: theme.palette.text.secondary }} />
        <YAxis type="number" dataKey="qnum" name="Question number"
               tick={tick} stroke={theme.palette.text.secondary} />
        <Tooltip contentStyle={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary, fontSize: 12,
        }}
                 content={({ payload }) => {
                   if (!payload || !payload.length) return null;
                   const p = payload[0].payload;
                   return (
                     <Box sx={{ p: 1, fontSize: 12,
                                bgcolor: 'background.paper',
                                border: '1px solid', borderColor: 'divider' }}>
                       <strong>{p.date} Q{p.qnum}</strong><br />
                       {p.time}<br />
                       {p.asker} → {p.portfolio}<br />
                     </Box>
                   );
                 }} />
        <Legend wrapperStyle={{ fontSize: 11,
                                color: theme.palette.text.primary }} />
        {dates.map((d, i) => (
          <Scatter key={d} name={d} data={byDate[d]}
                   fill={PALETTE[i % PALETTE.length]} fillOpacity={0.7} />
        ))}
      </ScatterChart>
    </ResponsiveContainer>
  );
}

export default function QtClock() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('qt-clock');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};
  const compl = c.completeness || { captured: 0, total: 0, pct: 0 };

  return (
    <ExploreStatsPage
      title="QT time-of-day"
      description="When each captured Question Time question actually began. House QT is scheduled to start at 14:00, but real start-times drift; this page plots question number against minutes-after-14:00 to reveal pace, drift, and outliers."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="qt-clock"
    >
      {data && (
        <Box>
          {compl.pct < 100 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              start_time completeness: {compl.captured} of {compl.total}{' '}
              questions have a captured start-time ({compl.pct}%). Completeness
              depends on the HouseLive admin marking each day as
              start-time-complete.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatCard label="Captured" value={compl.captured}
                        sub="questions with start_time" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Sitting days"
                        value={(c.earliest_latest || []).length} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Outliers"
                        value={(c.outliers || []).length}
                        sub="Qs outside standard window" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Completeness"
                        value={`${compl.pct}%`} />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Question start-time scatter (minutes after 14:00)
                </Typography>
                <QtScatter points={c.scatter || []} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  QT-start drift from 14:00 (mins)
                </Typography>
                <StatLine data={c.drift || []} xKey="date"
                          keys={['drift_min']} height={300} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Pace — minutes per question
                </Typography>
                <StatBar data={c.pace || []} xKey="date"
                         yKey="mins_per_q" height={300} />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Outliers — questions outside the 14:00–15:15 window
                </Typography>
                {(c.outliers || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No outliers — every captured question started inside the
                    standard QT window.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {(c.outliers || []).map((o, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 2,
                                         borderBottom: '1px solid',
                                         borderColor: 'divider', pb: 1 }}>
                        <Box sx={{ minWidth: 100, fontVariantNumeric: 'tabular-nums' }}>
                          {o.Date}
                        </Box>
                        <Box sx={{ minWidth: 80, fontVariantNumeric: 'tabular-nums' }}>
                          Q{o.Question}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {o.Asker} — {o.Portfolio}
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 110, textAlign: 'right' }}>
                          {o['Started at']} ({o['Mins after 14:00']} min)
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </ExploreStatsPage>
  );
}
